import { DateRange } from './DateFilterDropdown';

/**
 * Helper function to get date range for a preset value
 * This is used by QuickFilterButtons to calculate date ranges for presets
 */
export function getDateRangeForPreset(preset: string): DateRange {
  const now = new Date();
  const today = new Date(Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate()
  ));

  switch (preset) {
    case 'today': {
      const todayStart = new Date(Date.UTC(
        today.getUTCFullYear(),
        today.getUTCMonth(),
        today.getUTCDate()
      ));
      return { start: todayStart, end: todayStart };
    }

    case 'yesterday': {
      const yesterday = new Date(today);
      yesterday.setUTCDate(today.getUTCDate() - 1);
      return { start: yesterday, end: yesterday };
    }

    case 'overdue': {
      const currentYesterday = new Date(Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate() - 1,
        23, 59, 59, 999
      ));
      const epochStart = new Date(Date.UTC(1970, 0, 1, 0, 0, 0, 0));
      return { start: epochStart, end: currentYesterday };
    }

    case 'last7days': {
      const last7Days = new Date(today);
      last7Days.setUTCDate(last7Days.getUTCDate() - 7);
      return { start: last7Days, end: today };
    }

    case 'lastMonth': {
      const lastMonth = new Date(today);
      lastMonth.setUTCMonth(lastMonth.getUTCMonth() - 1);
      return { start: lastMonth, end: today };
    }

    case 'last3Months': {
      const last3Months = new Date(today);
      last3Months.setUTCMonth(last3Months.getUTCMonth() - 3);
      return { start: last3Months, end: today };
    }

    case 'last6Months': {
      const last6Months = new Date(today);
      last6Months.setUTCMonth(last6Months.getUTCMonth() - 6);
      return { start: last6Months, end: today };
    }

    case 'lastYear': {
      const lastYear = new Date(today);
      lastYear.setUTCFullYear(lastYear.getUTCFullYear() - 1);
      return { start: lastYear, end: today };
    }

    case 'thisWeek': {
      const dayOfWeek = today.getUTCDay();
      const thisWeekStart = new Date(today);
      thisWeekStart.setUTCDate(today.getUTCDate() - dayOfWeek);
      return { start: thisWeekStart, end: today };
    }

    case 'thisMonth': {
      const thisMonthStart = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1));
      return { start: thisMonthStart, end: today };
    }

    case 'thisYear': {
      const thisYearStart = new Date(Date.UTC(today.getUTCFullYear(), 0, 1));
      return { start: thisYearStart, end: today };
    }

    case 'currentWeek': {
      const dayOfWeek = today.getUTCDay();
      const thisWeekStart = new Date(today);
      thisWeekStart.setUTCDate(today.getUTCDate() - dayOfWeek);
      const thisWeekEnd = new Date(thisWeekStart);
      thisWeekEnd.setUTCDate(thisWeekStart.getUTCDate() + 6);
      return { start: thisWeekStart, end: thisWeekEnd };
    }

    case 'currentMonth': {
      const thisMonthStart = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1));
      const thisMonthEnd = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + 1, 0));
      return { start: thisMonthStart, end: thisMonthEnd };
    }

    case 'currentYear': {
      const thisYearStart = new Date(Date.UTC(today.getUTCFullYear(), 0, 1));
      const thisYearEnd = new Date(Date.UTC(today.getUTCFullYear(), 11, 31));
      return { start: thisYearStart, end: thisYearEnd };
    }

    case 'nextWeek': {
      const dayOfWeek = today.getUTCDay();
      const thisWeekStart = new Date(today);
      thisWeekStart.setUTCDate(today.getUTCDate() - dayOfWeek);
      const nextWeekStart = new Date(thisWeekStart);
      nextWeekStart.setUTCDate(thisWeekStart.getUTCDate() + 7);
      const nextWeekEnd = new Date(nextWeekStart);
      nextWeekEnd.setUTCDate(nextWeekStart.getUTCDate() + 6);
      return { start: nextWeekStart, end: nextWeekEnd };
    }

    case 'next7days': {
      const tomorrow = new Date(today);
      tomorrow.setUTCDate(today.getUTCDate() + 1);
      const next7End = new Date(today);
      next7End.setUTCDate(next7End.getUTCDate() + 7);
      return { start: tomorrow, end: next7End };
    }

    case 'next15days': {
      const next15Start = new Date(today);
      next15Start.setUTCDate(today.getUTCDate() + 1);
      const next15End = new Date(today);
      next15End.setUTCDate(next15End.getUTCDate() + 15);
      return { start: next15Start, end: next15End };
    }

    case 'next30days': {
      const next30Start = new Date(today);
      next30Start.setUTCDate(today.getUTCDate() + 1);
      const next30End = new Date(today);
      next30End.setUTCDate(next30End.getUTCDate() + 30);
      return { start: next30Start, end: next30End };
    }

    case 'next90days': {
      const next90Start = new Date(today);
      next90Start.setUTCDate(today.getUTCDate() + 1);
      const next90End = new Date(today);
      next90End.setUTCDate(next90End.getUTCDate() + 90);
      return { start: next90Start, end: next90End };
    }

    case 'nextMonth': {
      const nextMonthStart = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + 1, 1));
      const nextMonthEnd = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + 2, 0));
      return { start: nextMonthStart, end: nextMonthEnd };
    }

    case 'next3Months': {
      const next3MonthsStart = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + 1, 1));
      const next3MonthsEnd = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + 4, 0));
      return { start: next3MonthsStart, end: next3MonthsEnd };
    }

    case 'next6Months': {
      const next6MonthsStart = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + 1, 1));
      const next6MonthsEnd = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + 7, 0));
      return { start: next6MonthsStart, end: next6MonthsEnd };
    }

    case 'nextYear': {
      const nextYearStart = new Date(Date.UTC(today.getUTCFullYear() + 1, 0, 1));
      const nextYearEnd = new Date(Date.UTC(today.getUTCFullYear() + 1, 11, 31));
      return { start: nextYearStart, end: nextYearEnd };
    }

    default:
      return { start: null, end: null };
  }
}

